import React, { useEffect, useState, useMemo, Fragment, useRef } from "react";
import { Col, Row, Table, Collapse, Badge, Form } from "react-bootstrap";
import avatar from "../../../Assets/img/patient.png";
import { CONFIGS, MODULES } from "../../../Constants/appConstants";
import * as storageConstants from "../../../Constants/storageConstant";
import { NO_MOBILE_NUMBER } from "../../../Constants/stringConstants";
import { IMAGE_URL } from "../../../Constants/urlConstants";
import * as commonServices from "../../../Services/common";
import { changeDateFormatForDisplay } from "../../../Utils/dateUtils";
import canAccessModule from "../../../Utils/permissionUtils";
import { slice } from "../../../Utils/stringUtils";
import IconResource from "../../../Wrapper/IconResource/IconResource";
import ImageResource from "../../../Wrapper/ImageResource/ImageResource";
import Button from "../../_widgets/Button/Button";
import DateRangePicker from "../../_widgets/DateRangePicker/DateRangePicker";
import Dropdown from "../../_widgets/Dropdown/Dropdown";
import Input from "../../_widgets/Input/Input";
import MainLoader from "../../_widgets/Loader/MainLoader";
import NavBar from "../../_widgets/NavBar/NavBar";
import CustomPagination from "../../_widgets/Pagination/Pagination";
import BasicModal from "../../_widgets/Modal/BasicModal";
import Text from "../../_widgets/Text/Text";
import CustomTooltip from "../../_widgets/Tooltip/CustomTooltip";
import AnimatedPage from "../../AnimatedPage/AnimatedPage";
import "./TrackPatient.scss";

const TrackPatientUI = ({
  filters,
  setFilters,
  openDetailsPage,
  loading,
  patientsList,
  getPatientsList,
  allDiagnosis,
  onPageChange,
  resetFilters,
  onDownloadClick,
  // Modal specific props
  isModal = false,
  modalShow = false,
  handleClose,
  selectionMode = false,
  selectedNumbers = [],
  setSelectedNumbers,
  handleSaveSelection,
  isSelectAll = false,
  selectAll,
}) => {
  const [showFilters, setShowFilters] = useState(true);
  const [checkRequired, setCheckRequired] = useState({
    aadhar_no: false,
    mno: false,
    file_no: false,
    online_id: false,
  });

  const {
    startRange,
    endRange,
    activeStatus,
    selectedMedicines,
    selectedServices,
    selectedGender,
    selectedCity,
    selectedDiagnosis,
    selectedDoctor,
    ageRange,
  } = filters;

  const updateFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    let configData = commonServices.getLocalData(storageConstants.CONFIG);
    let data = {
      aadhar_no: false,
      mno: false,
      file_no: false,
      online_id: false,
      city: false,
      address: false,
    };
    configData?.forEach((config) => {
      if (config.key === CONFIGS.AADHAR_NO && config.mandatory === "true" && config.show === "true")
        data.aadhar_no = true;
      if (config.key === "mobile_no" && config.mandatory === "true" && config.show === "true")
        data.mno = true;
      if (config.key === CONFIGS.FILE_NO && config.mandatory === "true" && config.show === "true")
        data.file_no = true;
      if (config.key === CONFIGS.ONLINE_ID && config.mandatory === "true" && config.show === "true")
        data.online_id = true;
      if (config.key === CONFIGS.ADDRESS && config.mandatory === "true" && config.show === "true")
        data.address = true;
      if (config.key === CONFIGS.CITY && config.mandatory === "true" && config.show === "true")
        data.city = true;
    });
    setCheckRequired(data);
  }, []);

  const [isSticky, setIsSticky] = useState(false);
  const topBarRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When entry is NOT intersecting, it has scrolled out of view, so show sticky bar
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: [0],
      },
    );

    if (topBarRef.current) {
      observer.observe(topBarRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const scrollToFilters = () => {
    topBarRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const activeFilters = useMemo(() => {
    const active = [];
    if (startRange.startDate) active.push("Start Range");
    if (endRange.startDate) active.push("End Range");
    if (selectedMedicines.length > 0) active.push(`Medicines (${selectedMedicines.length})`);
    if (selectedServices.length > 0) active.push(`Services (${selectedServices.length})`);
    if (selectedDoctor.length > 0) active.push("Doctor");
    if (activeStatus !== "active") active.push(activeStatus);
    return active;
  }, [filters]);

  const detailedActiveFilters = useMemo(() => {
    const list = [];

    // 1. Status
    if (activeStatus && activeStatus !== "active") {
      list.push({
        label: "Status",
        value: activeStatus,
      });
    }

    // 2. Start Date / Date Range
    if (startRange?.startDate) {
      const start = changeDateFormatForDisplay(startRange.startDate);
      const end = startRange.endDate ? changeDateFormatForDisplay(startRange.endDate) : "Any";
      list.push({
        label:
          activeStatus === "active" || activeStatus === "Followup" ? "Date Range" : "Start Range",
        value: `${start} - ${end}`,
      });
    }

    // 3. End Date Range
    if (activeStatus !== "active" && activeStatus !== "Followup" && endRange?.startDate) {
      const start = changeDateFormatForDisplay(endRange.startDate);
      const end = endRange.endDate ? changeDateFormatForDisplay(endRange.endDate) : "Any";
      list.push({
        label: "End Range",
        value: `${start} - ${end}`,
      });
    }

    // Helper to get selected labels from a dynamic option list
    const getLabels = (selectedList, options) => {
      if (!selectedList || selectedList.length === 0) return null;
      return selectedList
        .map((val) => options.find((opt) => String(opt.value) === String(val))?.label)
        .filter(Boolean)
        .join(", ");
    };

    // 4. Medicines
    const medLabels = getLabels(selectedMedicines, commonServices.getMedicineOptions() || []);
    if (medLabels) {
      list.push({ label: "Medicines", value: medLabels });
    }

    // 5. Services
    const serLabels = getLabels(selectedServices, commonServices.getServiceOptions() || []);
    if (serLabels) {
      list.push({ label: "Services", value: serLabels });
    }

    // 6. Gender
    const genLabels = getLabels(selectedGender, commonServices.getGenderOptions() || []);
    if (genLabels) {
      list.push({ label: "Gender", value: genLabels });
    }

    // 7. City
    if (commonServices.isConfigEnable(CONFIGS.CITY)?.[0]) {
      const cityLabels = getLabels(selectedCity, commonServices.getCityOptions() || []);
      if (cityLabels) {
        list.push({ label: "City", value: cityLabels });
      }
    }

    // 8. Diagnosis
    const diagLabels = getLabels(
      selectedDiagnosis,
      commonServices.getDiagnosisOptions(allDiagnosis) || [],
    );
    if (diagLabels) {
      list.push({ label: "Diagnosis", value: diagLabels });
    }

    // 9. Doctor
    const docLabels = getLabels(selectedDoctor, commonServices.getDoctorOptions() || []);
    if (docLabels) {
      list.push({ label: "Doctor", value: docLabels });
    }

    // 10. Age Range
    if (ageRange?.from || ageRange?.to) {
      const from = ageRange.from || "0";
      const to = ageRange.to || "Any";
      list.push({ label: "Age", value: `${from} to ${to}` });
    }

    return list;
  }, [filters, allDiagnosis]);

  const renderContent = () => (
    <div className="patient_list">
      <div className="top_bar" ref={topBarRef}>
        {!isModal && !showFilters && activeFilters.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-3">
              <div className="active_filters_summary d-flex gap-2 align-items-center">
                <span className="text-muted small me-1">Active Filters:</span>
                {activeFilters.slice(0, 3).map((f) => (
                  <Badge key={f} bg="primary" pill>
                    {f}
                  </Badge>
                ))}
                {activeFilters.length > 3 && (
                  <Badge bg="secondary" pill>
                    +{activeFilters.length - 3} more
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        <Collapse in={showFilters}>
          <div className="filter_section">
            <Row className="gx-4">
              <Col lg={12} className="mb-4">
                <div className="status_pill_group">
                  {[
                    {
                      key: "active",
                      label: "All",
                      tooltip:
                        "Shows all patients matching your search criteria. Example: Show all male patients from Delhi.",
                    },
                    {
                      key: "Followup",
                      label: "Followup",
                      tooltip:
                        "Patients whose scheduled checkup falls within the selected Date Range.",
                    },
                    {
                      key: "Regular",
                      label: "Regular",
                      tooltip:
                        "Patients who visited in both ranges. Example: Visited in Jan AND returned again in Feb.",
                    },
                    {
                      key: "Non-Regular",
                      label: "Non-Regular",
                      tooltip:
                        "Visited in the 'Start Range' but have NOT returned in the 'End Range'. Example: Visited in Jan but missing in Feb.",
                    },
                    {
                      key: "Re-Active",
                      label: "Re-Active",
                      tooltip:
                        "Did NOT visit in the 'Start Range' but returned in the 'End Range'. Example: Skipped Jan but came back in Feb.",
                    },
                  ].map((status) => (
                    <Fragment key={status.key}>
                      <CustomTooltip text={status.tooltip} placement="top" type="tooltip">
                        <label className="status_pill">
                          <input
                            type="radio"
                            name="status"
                            checked={activeStatus === status.key}
                            onChange={() => updateFilters("activeStatus", status.key)}
                          />
                          <span className="pill_label">{status.label}</span>
                        </label>
                      </CustomTooltip>
                    </Fragment>
                  ))}
                </div>
                {/* Dynamic info text outside the pill box */}
                {[
                  {
                    key: "active",
                    tooltip:
                      "All visits within the selected date range. If no dates are picked, it defaults to today's visits.",
                  },
                  {
                    key: "Followup",
                    tooltip:
                      "Patients whose scheduled checkup falls within the selected 'Date Range'.",
                  },
                  {
                    key: "Regular",
                    tooltip: "Patients who visited in BOTH the 'Start Range' AND the 'End Range'.",
                  },
                  {
                    key: "Non-Regular",
                    tooltip:
                      "Visited in the 'Start Range' but have NOT returned in the 'End Range'.",
                  },
                  {
                    key: "Re-Active",
                    tooltip: "Did NOT visit in the 'Start Range' but returned in the 'End Range'.",
                  },
                ].map(
                  (status) =>
                    activeStatus === status.key && (
                      <div key={status.key} className="status_info_text mt-2 small text-muted">
                        {status.tooltip}
                      </div>
                    ),
                )}
              </Col>

              {/* Show primary Date Range for all statuses. Comparative statuses like 'Regular' show a second range. */}
              <Col
                xxl={isModal ? 6 : 4}
                xl={isModal ? 6 : 6}
                lg={isModal ? 6 : 6}
                md={12}
                className="mb-4"
              >
                <label className="filter_label">
                  {activeStatus === "active" || activeStatus === "Followup"
                    ? "Date Range"
                    : "Start Range"}
                </label>
                <DateRangePicker
                  startDate={startRange?.startDate}
                  endDate={startRange?.endDate}
                  onChange={(s, e) => updateFilters("startRange", { startDate: s, endDate: e })}
                  allowFutureDates={true}
                  showDateShortcuts={true}
                />
              </Col>

              {activeStatus !== "active" && activeStatus !== "Followup" && (
                <Col
                  xxl={isModal ? 6 : 4}
                  xl={isModal ? 6 : 6}
                  lg={isModal ? 6 : 6}
                  md={12}
                  className="mb-4"
                >
                  <label className="filter_label">End Range</label>
                  <DateRangePicker
                    startDate={endRange?.startDate}
                    endDate={endRange?.endDate}
                    onChange={(s, e) => updateFilters("endRange", { startDate: s, endDate: e })}
                    allowFutureDates={true}
                    showDateShortcuts={true}
                  />
                </Col>
              )}

              {canAccessModule(MODULES.MEDICINES_MODULE) && (
                <Col lg={isModal ? 6 : 3} md={6} className="mb-3">
                  <Dropdown
                    isMulti
                    label="Medicine(s)"
                    placeholder="Medicine"
                    name={selectedMedicines}
                    onChange={(v) => updateFilters("selectedMedicines", v)}
                    options={commonServices.getMedicineOptions()}
                  />
                </Col>
              )}
              {canAccessModule(MODULES.SERVICIES_MODULE) && (
                <Col lg={isModal ? 6 : 3} md={6} className="mb-3">
                  <Dropdown
                    isMulti
                    label="Service(s)"
                    placeholder="Service"
                    name={selectedServices}
                    onChange={(v) => updateFilters("selectedServices", v)}
                    options={commonServices.getServiceOptions()}
                  />
                </Col>
              )}
              <Col lg={isModal ? 6 : 3} md={6} className="mb-3">
                <Dropdown
                  isMulti={true}
                  label="Gender"
                  placeholder="Gender"
                  name={selectedGender}
                  onChange={(v) => updateFilters("selectedGender", v)}
                  options={commonServices.getGenderOptions()}
                />
              </Col>
              {commonServices.isConfigEnable(CONFIGS.CITY)?.[0] && (
                <Col lg={isModal ? 6 : 3} md={6} className="mb-3">
                  <Dropdown
                    isMulti={true}
                    label="City"
                    placeholder="City"
                    name={selectedCity}
                    onChange={(v) => updateFilters("selectedCity", v)}
                    options={commonServices.getCityOptions()}
                  />
                </Col>
              )}
              {canAccessModule(MODULES.DIAGNOSIS) && (
                <Col lg={isModal ? 6 : 3} md={6} className="mb-3">
                  <Dropdown
                    isMulti
                    label="Diagnosis"
                    placeholder="Diagnosis"
                    name={selectedDiagnosis}
                    onChange={(v) => updateFilters("selectedDiagnosis", v)}
                    options={commonServices.getDiagnosisOptions(allDiagnosis)}
                  />
                </Col>
              )}
              <Col lg={isModal ? 6 : 3} md={6} className="mb-3">
                <Dropdown
                  isMulti
                  label="Doctor"
                  placeholder="Doctor"
                  name={selectedDoctor}
                  onChange={(v) => updateFilters("selectedDoctor", v)}
                  options={commonServices.getDoctorOptions()}
                />
              </Col>

              <Col lg={isModal ? 6 : 3} md={6} className="mb-3">
                <div className="age-range-container">
                  <label className="filter_label">Age Range</label>
                  <div className="d-flex align-items-center gap-2">
                    <Input
                      placeholder="From"
                      value={ageRange.from}
                      onChange={(e) =>
                        updateFilters("ageRange", { ...ageRange, from: e.target.value })
                      }
                    />
                    <span className="text-muted small">to</span>
                    <Input
                      placeholder="To"
                      value={ageRange.to}
                      onChange={(e) =>
                        updateFilters("ageRange", { ...ageRange, to: e.target.value })
                      }
                    />
                  </div>
                </div>
              </Col>

              {isModal && (
                <Col lg={6} md={6} className="mb-3 d-flex align-items-end gap-2">
                  <Button
                    buttonType="primary"
                    onClick={() => getPatientsList(1)}
                    text="Search"
                    isLoading={loading}
                    prefixIcon={<IconResource type="search" size={14} />}
                  />
                  <Button
                    buttonType="secondary"
                    onClick={resetFilters}
                    text="Reset"
                    prefixIcon={<IconResource type="refresh" size={14} />}
                  />
                </Col>
              )}
            </Row>
          </div>
        </Collapse>
      </div>

      {/* Sticky thin strip with read-only filters shown when scrolled */}
      {!isModal && isSticky && detailedActiveFilters.length > 0 && (
        <div className="sticky_filter_strip">
          <div className="sticky_filter_strip__content">
            <span className="sticky_filter_strip__title">
              <IconResource type="filter" size={14} color="var(--primary)" />
              Active Filters
            </span>
            <div className="sticky_filter_strip__pills">
              {detailedActiveFilters.map((f, i) => (
                <div key={i} className="sticky_filter_strip__pill">
                  <span className="pill_name">{f.label}:</span>
                  <span className="pill_val">{f.value}</span>
                </div>
              ))}
            </div>
            <button className="sticky_filter_strip__btn" onClick={scrollToFilters}>
              <IconResource type="edit" size={12} />
              Edit
            </button>
          </div>
        </div>
      )}

      <div className="custom_table_card position-relative">
        {loading ? (
          <MainLoader />
        ) : patientsList?.data?.length > 0 ? (
          <>
            <AnimatedPage>
              <div className="custom_table">
                <Table responsive hover>
                  <thead>
                    <tr>
                      {selectionMode && <th></th>}
                      <th>Patient Name</th>
                      <th>Mobile No</th>
                      <th>Info & Address</th>
                      <th>Item(s)</th>
                      <th>Last visit date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientsList?.data?.map((data) => (
                      <tr
                        key={data.id}
                        onClick={() =>
                          !selectionMode ? window.open(`/patient-visit/${data.id}`, "_blank") : null
                        }
                        style={{ cursor: selectionMode ? "default" : "pointer" }}
                      >
                        {selectionMode && (
                          <td>
                            <Form.Check
                              id={`chk-${data.id}`}
                              type="checkbox"
                              checked={selectedNumbers.some(
                                (item) => item.phone_number === data?.patient?.mno,
                              )}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                if (isChecked) {
                                  setSelectedNumbers((prev) => [
                                    ...prev,
                                    {
                                      name: data?.patient?.name,
                                      phone_number: data?.patient?.mno,
                                      isChecked: true,
                                      allow_broadcast: true,
                                    },
                                  ]);
                                } else {
                                  setSelectedNumbers((prev) =>
                                    prev.filter((item) => item.phone_number !== data?.patient?.mno),
                                  );
                                }
                              }}
                            />
                          </td>
                        )}
                        <td>
                          <div className="patient_name d-flex align-items-center">
                            <div className="profile_picture me-3">
                              <div className="position-relative d-flex align-items-center">
                                <img
                                  src={
                                    data?.patient?.photo_url
                                      ? IMAGE_URL + data?.patient?.photo_url
                                      : avatar
                                  }
                                  onError={(event) => commonServices.handleImageError(event)}
                                  alt="Patient Photo"
                                  className="rounded-circle border"
                                  style={{
                                    width: "44px",
                                    height: "44px",
                                    objectFit: "cover",
                                  }}
                                />
                                <div
                                  className={`position-absolute ${data.mno === null && "reduce_opacity"} `}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    if (data?.mno) {
                                      window.open(
                                        commonServices.createWhatsAppLink(data?.mno, data?.name),
                                      );
                                    }
                                  }}
                                  style={{
                                    bottom: "-4px",
                                    right: "-4px",
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                    padding: "2px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  <CustomTooltip
                                    text={data?.mno === null && NO_MOBILE_NUMBER}
                                    type="tooltip"
                                    placement="top"
                                  >
                                    <IconResource type="whatsapp" size={16} />
                                  </CustomTooltip>
                                </div>
                              </div>
                            </div>
                            <div className="name_id d-flex flex-column">
                              <CustomTooltip
                                type="tooltip"
                                text={data?.patient?.name?.toUpperCase()}
                              >
                                <strong>
                                  {slice(data?.patient?.name?.toUpperCase() || "-", 15)}
                                </strong>
                              </CustomTooltip>
                              <span className="small text-muted">
                                {commonServices.getPatientIdentifier(data?.id, data)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{data?.patient?.mno || "-"}</td>
                        <td>
                          <div className="d-flex flex-column">
                            <span>
                              {checkRequired.aadhar_no
                                ? data?.patient?.aadhar_no || "-"
                                : checkRequired.mno
                                  ? `M. No.: ${data?.patient?.mno || "-"}`
                                  : checkRequired.file_no
                                    ? data?.patient?.file_no || "-"
                                    : data?.patient?.online_id?.toUpperCase() || "-"}
                            </span>
                            <span className="small text-muted">
                              {data?.patient?.address || "-"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: "200px" }}>
                            {data?.orders?.[0] !== undefined &&
                            data?.orders?.[0]?.products?.length > 0
                              ? data?.orders?.[0]?.products
                                  .map((product) => product.item_detail?.name)
                                  .filter(Boolean)
                                  .join(", ")
                              : "-"}
                          </div>
                        </td>
                        <td>
                          <Badge bg="light" text="dark" className="border font-weight-normal">
                            {data?.visit_date ? changeDateFormatForDisplay(data?.visit_date) : "-"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </AnimatedPage>
            <div className="pt-3">
              <CustomPagination
                totalData={patientsList?.total}
                pageLength={patientsList?.per_page}
                activePage={patientsList?.current_page}
                onPageChange={onPageChange}
                lastPage={patientsList.last_page}
              />
            </div>
          </>
        ) : (
          <div className="no_data text-center py-5">
            <ImageResource type="noData" style={{ width: "240px" }} className="mb-3" />
            <h4 className="text-muted">No patients found matches your criteria.</h4>
            <p className="text-muted small">Try adjusting your filters or search keywords.</p>
          </div>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <BasicModal
        show={modalShow}
        size="xl"
        handleClose={handleClose}
        heading={"Select Patient"}
        footer
        button2Click={() => handleSaveSelection(selectedNumbers)}
        button2Text={"Save"}
        button1Click={() => selectAll()}
        button1Text={isSelectAll ? "Unselect All" : "Select All"}
        minH="80vh"
      >
        <div className="track_patient">{renderContent()}</div>
      </BasicModal>
    );
  }

  return (
    <div className="track_patient">
      <AnimatedPage>
        <NavBar
          heading={"Track Patient"}
          onClick={() => {
            document.body.classList.toggle("side_active");
            document.body.classList.toggle("side_active_mb");
          }}
          Icon="menu"
          button="Search"
          buttonIcon="search"
          buttononclick={() => getPatientsList(1)}
          buttonType="primary"
          button2="Reset"
          buttonIcon2="refresh"
          button2onclick={resetFilters}
          buttonType2="secondary"
          button3={showFilters ? "Hide Filters" : "Show Filters"}
          buttonIcon3={showFilters ? "eye-off" : "filter"}
          button3onclick={() => setShowFilters(!showFilters)}
          buttonType3="secondary"
          button4={onDownloadClick ? "Download" : null}
          buttonIcon4="download"
          button4onclick={onDownloadClick}
          buttonType4="secondary"
        />
        {renderContent()}
      </AnimatedPage>
    </div>
  );
};

export default TrackPatientUI;
